import { Button, Css, IconButton, SubmitButton, useModal, useTestIds } from "@homebound/beam";
import { Observer } from "mobx-react";
import { useEffect, useMemo } from "react";
import {
  lineTypeDetails,
  lineTypeOf,
} from "src/routes/libraries/plan-package-v2/components/takeoffs/side-panel/lineTypes";
import { TakeoffsSidePanel } from "src/routes/libraries/plan-package-v2/components/takeoffs/side-panel/TakeoffsSidePanel";
import {
  TakeoffsSidePanelMode,
  useTakeoffsSidePanel,
} from "src/routes/libraries/plan-package-v2/components/takeoffs/side-panel/useTakeoffsSidePanel";
import {
  AddEditItemModal_TliFragment,
  useAddEditItemModal_GetTliQuery,
} from "src/routes/libraries/plan-package/takeoffs/components/AddItems/AddEditItemModal.generated";
import { LaborFields } from "src/routes/libraries/plan-package/takeoffs/components/AddItems/LaborFields";
import { MaterialFields } from "src/routes/libraries/plan-package/takeoffs/components/AddItems/MaterialFields";
import { PlaceholderFields } from "src/routes/libraries/plan-package/takeoffs/components/AddItems/PlaceholderFields";
import {
  duplicateTliData,
  LineType,
  PublishedTakeoffBanner,
  saveAddEditItem,
  useAddEditItemModalFormState,
  useTakeoffRemovalsEnabled,
} from "src/routes/libraries/plan-package/takeoffs/components/AddItems/utils";
import { DeleteItemsConfirmationModal } from "src/routes/libraries/plan-package/takeoffs/components/DeleteItemsConfirmationModal";
import {
  useTakeoffsManagerContext,
  useTakeoffsStore,
} from "src/routes/libraries/plan-package/takeoffs/TakeoffsManagerContext";
import { queryResult } from "src/utils";

type TakeoffLinePanelProps = { mode: TakeoffsSidePanelMode };

/** Edits a line, or adds one: empty, or pre-filled by Duplicate. */
export function TakeoffLinePanel(props: TakeoffLinePanelProps) {
  const { mode } = props;
  if (mode.kind === "add") return <TakeoffLinePanelView mode={mode} type={mode.type} />;
  return <ExistingLinePanel mode={mode} />;
}

type ExistingLinePanelProps = { mode: Exclude<TakeoffsSidePanelMode, { kind: "add" }> };

/** Loads the line for the version on screen, so a save that moves to a new draft re-loads it from there. */
function ExistingLinePanel(props: ExistingLinePanelProps) {
  const { mode } = props;
  const versionId = useTakeoffsStore((state) => state.planPackage.version.id);
  const query = useAddEditItemModal_GetTliQuery({ variables: { id: mode.tliId, versionId } });
  return queryResult(query, ({ takeoffLineItem }) => (
    <TakeoffLinePanelView mode={mode} type={lineTypeOf(takeoffLineItem)} tli={takeoffLineItem} />
  ));
}

type TakeoffLinePanelViewProps = { mode: TakeoffsSidePanelMode; type: LineType; tli?: AddEditItemModal_TliFragment };

function TakeoffLinePanelView(props: TakeoffLinePanelViewProps) {
  const { mode, type, tli } = props;
  const store = useTakeoffsManagerContext();
  const planPackageId = useTakeoffsStore((state) => state.planPackage.id);
  const readOnly = !useTakeoffsStore((state) => state.planPackage.canEditLineItems.allowed);
  const takeoffRemovalsEnabled = useTakeoffRemovalsEnabled();
  const { closePanel, showLine, lineDeleted, registerDirtyCheck } = useTakeoffsSidePanel();
  const { openModal } = useModal();
  const tid = useTestIds(props, "takeoffLinePanel");
  // Memoized so form-state only re-maps the line when the query returns a new one
  const initialTliData = useMemo(
    () => tli && (mode.kind === "duplicate" ? duplicateTliData(tli) : tli),
    [tli, mode.kind],
  );
  const formState = useAddEditItemModalFormState(type, readOnly, initialTliData);
  useEffect(() => registerDirtyCheck(() => formState.dirty), [registerDirtyCheck, formState]);

  const { label, icon } = lineTypeDetails[type];
  const isNew = mode.kind !== "edit";

  async function save() {
    const { saveTli, itemTableApi } = store.getState();
    const result = await saveAddEditItem({ formState, saveTli, isPlaceholderItem: type === "placeholder" });
    const { takeoffLineItems = [], groupTotals = [] } = result.data?.saveTakeoffLineItems ?? {};
    const added = takeoffLineItems.first;
    // Edits stay on their line; an add moves to the new one
    if (!isNew || !added) return;
    // Expand the new line's group if collapsed. `isCollapsedRow` throws on groups without a row, e.g. the page total
    const groupRowIds = itemTableApi?.getVisibleRows("groupBy").map((row) => row.id) ?? [];
    groupTotals
      .filter((gt) => gt.tliIdsInGroup.includes(added.id) && groupRowIds.includes(gt.groupId))
      .filter((gt) => itemTableApi?.isCollapsedRow(gt.groupId))
      .forEach((gt) => itemTableApi?.toggleCollapsedRow(gt.groupId));
    showLine(added.id);
    // Give the save's cache update a moment to render the new row
    setTimeout(() => {
      const index = itemTableApi?.getVisibleRows().findIndex((row) => row.kind === "item" && row.id === added.id) ?? -1;
      // Centered, since "start" tucks the row under the sticky headers
      if (index !== -1) itemTableApi?.scrollToIndex({ index, align: "center", behavior: "smooth" });
    }, 100);
  }

  function createFooter() {
    if (readOnly) return { end: <Button label="Close" onClick={closePanel} /> };
    const cancel = <Button variant="secondary" label="Cancel" onClick={closePanel} />;
    if (isNew) {
      return {
        end: (
          <>
            {cancel}
            {/* Not `SubmitButton`, which enables a new entity before it's valid */}
            <Observer>
              {() => (
                <Button
                  label="Add"
                  disabled={!formState.valid && "Fill in the required fields"}
                  onClick={save}
                  {...tid.add}
                />
              )}
            </Observer>
          </>
        ),
      };
    }
    return {
      start: (
        <IconButton
          icon="trash"
          label="Delete"
          {...tid.delete}
          onClick={() =>
            openModal({
              content: (
                <DeleteItemsConfirmationModal
                  store={store}
                  itemIdsToDelete={[mode.tliId]}
                  onDeleted={() => lineDeleted(mode.tliId)}
                />
              ),
            })
          }
        />
      ),
      end: (
        <>
          {cancel}
          <SubmitButton label="Save" form={formState} onClick={save} />
        </>
      ),
    };
  }

  const fieldProps = {
    store,
    formState,
    planPackageId,
    initialTliData,
    readOnly,
    takeoffRemovalsEnabled,
    inPanel: true,
  };

  return (
    <TakeoffsSidePanel
      tag={{ icon, text: label }}
      title={isNew ? `Add ${label}` : (initialTliData?.name ?? "")}
      onClose={closePanel}
      footer={createFooter()}
    >
      <div css={Css.df.fdc.gap2.$}>
        {readOnly && <PublishedTakeoffBanner />}
        {type === "labor" ? (
          <LaborFields {...fieldProps} />
        ) : type === "material" ? (
          <MaterialFields {...fieldProps} />
        ) : (
          <PlaceholderFields {...fieldProps} isEditing={!isNew} />
        )}
      </div>
    </TakeoffsSidePanel>
  );
}
